import './index.css'

import {
  DISCOVERY_SUITE,
  WATERMARKING,
} from '@disco/constants/billing-provider-components'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
import { toCents } from '@disco/utils/pricing'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import scrollIntoView from 'scroll-into-view-if-needed'
import {
  DS_QUOTAS,
  loadBillingProviderComponents,
} from '@disco/state-management/actions/billing-provider-components'
import {
  getSubscription,
  getSubscriptionPeriod,
} from '@disco/state-management/selectors/subscription'
import { getCorePlan } from '@disco/state-management/selectors/user'
import DiscoverySuiteDescription from 'components/common/subscribed-user-billing/add-ons/discovery-suite-description'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Card from 'components/ui/card'
import Collapsible from 'components/ui/collapsible'
import ContactUsLink from 'components/ui/contact-us-link'
import CouponCode from 'components/ui/coupon-code'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Icon from 'components/ui/icon'
import PricingCounter from 'components/ui/pricing-counter'
import Tooltip from 'components/ui/tooltip'
import { useDynamicTracksOptions } from 'pages/settings/billing/dynamicPricingOptions'
import { fetchComponentCosts } from './estimates'

export type ContactInformation = {
  address: string | null | undefined
  address_2: string | null | undefined
  city: string | null | undefined
  country: string | null | undefined
  state: string | null | undefined
  zip: string | null | undefined
  reference: string | null | undefined
}

type Props = {
  tracksQuota?: number
  quota: number
  quotaUsed: number
  unsubscribeAvailable: boolean
  disabled: boolean
  disabledUpdatePaymentDetails: boolean
  quotaUpdating: boolean
  enableDS?: boolean
  enableWM?: boolean
  enableChange: boolean
  client_libraries_trial?: boolean
  auto_tagged_tracks_trial?: boolean
  type: typeof DISCOVERY_SUITE | typeof WATERMARKING
  contactInformation: ContactInformation
  isModalExplorerRoute?: boolean | null | undefined
  trackId?: number | null | undefined
}

const removePriceInOptions = options => {
  return options.map(option => ({ ...option, amount: 0, nodeRight: null }))
}

/**
 * AddOns component for managing subscription add-ons (Discovery Suite and Watermarking)
 *
 * A comprehensive component that handles add-on subscription management including
 * quota selection, pricing calculation, coupon application, and purchase flow.
 * Supports both Discovery Suite and Watermarking add-ons with dynamic pricing
 * and usage tracking.
 *
 * @param props - Component props
 * @param props.tracksQuota - Total tracks quota from base plan
 * @param props.quota - Current add-on quota
 * @param props.quotaUsed - Amount of quota currently used
 * @param props.unsubscribeAvailable - Whether unsubscribe option is available
 * @param props.disabled - Whether add-on changes are disabled
 * @param props.disabledUpdatePaymentDetails - Whether payment updates are disabled
 * @param props.quotaUpdating - Whether quota is currently updating
 * @param props.enableDS - Whether Discovery Suite is enabled
 * @param props.enableWM - Whether Watermarking is enabled
 * @param props.enableChange - Whether changes are allowed
 * @param props.client_libraries_trial - Whether client libraries trial is active
 * @param props.auto_tagged_tracks_trial - Whether auto-tagging trial is active
 * @param props.type - Type of add-on (Discovery Suite or Watermarking)
 * @param props.contactInformation - Billing contact information
 * @param props.isModalExplorerRoute - Whether opened from modal explorer route
 * @param props.trackId - Track ID for modal context
 * @returns JSX element containing the add-on management interface
 */
const AddOns = ({
  tracksQuota = 0,
  quota,
  quotaUsed,
  unsubscribeAvailable,
  disabled,
  disabledUpdatePaymentDetails,
  quotaUpdating,
  enableDS,
  enableWM,
  enableChange,
  client_libraries_trial,
  auto_tagged_tracks_trial,
  type,
  contactInformation,
  isModalExplorerRoute,
  trackId,
}: Props): React.ReactNode => {
  const isWM = type === WATERMARKING
  const dispatch = useDispatch()
  const subscription = useSelector(getSubscription)
  const planPeriod = useSelector(getSubscriptionPeriod)
  const corePlan = useSelector(getCorePlan)
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )
  const [openCoupon, setOpenCoupon] = useState(false)
  const [couponComponent, setCouponComponent] = useState<Record<
    string,
    any
  > | null>(null)
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const overQuota = quotaUsed > quota
  const [showOptions, setShowOptions] = useState(overQuota || !quota)
  const tracksDynamicOptions = useDynamicTracksOptions({
    customTrackAllowance: quota === Infinity ? 0 : quota,
    customTrackCount: quotaUsed,
    customLimits: isWM
      ? [
          // TODO: remove data hardcoding in component and read from pricingForm, like AddOns component did
          {
            value: 1,
            label: '1 watermarked playlist',
          },
          {
            value: 3,
            label: '3 watermarked playlists',
          },
          {
            value: UNLIMITED_WATERMARKING_QUOTA,
            label: 'Unlimited watermarked playlists',
          },
        ]
      : DS_QUOTAS.map(quota => quota * 1000),
    customType: type,
    customDisabledCondition: isWM
      ? undefined
      : v => v > tracksQuota && tracksQuota > 0,
    quotaIsNotScalable: isWM,
  })
  const [options, setOptions] = useState(tracksDynamicOptions.options)
  const [value, setValue] = useState(
    options.find(({ value }) => value === quota),
  )
  const [featureCostWithTax, setFeatureCostWithTax] = useState(0)
  const [featureCost, setFeatureCost] = useState(0)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const submitDisabled =
    !value ||
    (value && value.value === quota) ||
    disabled ||
    loadingPreview ||
    disabledUpdatePaymentDetails ||
    quotaUpdating
  let submitCaption: React.ReactNode = isWM
    ? 'Add to Plan'
    : 'Add Discovery Suite'
  let submitIntercomMsg = isWM
    ? "I'd like to add Watermarking to my subscription"
    : "I'd like to add Discovery Suite to my subscription"

  if (!client_libraries_trial && !auto_tagged_tracks_trial && enableDS) {
    submitCaption = 'Change Discovery Suite'
    submitIntercomMsg = "I'd like to change my Discovery Suite subscription"
  }

  if (enableWM) {
    submitCaption = 'Change Watermarking Plan'
    submitIntercomMsg = "I'd like to change my Watermarking subscription"
  }

  if (!enableChange) {
    submitCaption = (
      <ContactUsLink
        className="AddOns_ContactUsLink"
        intercomMsg={submitIntercomMsg}
      >
        {submitCaption}
      </ContactUsLink>
    )
  }

  useEffect(() => {
    dispatch(loadBillingProviderComponents())
  }, [])
  useEffect(() => {
    if (window?.location?.hash) {
      const DOMElement = document.getElementById(
        window.location.hash.replace('#', ''),
      )

      if (DOMElement) {
        scrollIntoView(DOMElement, {
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [])

  const getPreview = async (coupon?: string) => {
    setLoadingPreview(true)
    if (billingProviderComponents.loading || !billingProviderComponents.loaded)
      return {}

    try {
      const { preview, newFeatureCost, newFeatureCostWithTax } =
        await fetchComponentCosts({
          subscription,
          billingProviderComponents,
          type,
          value,
          quota,
          corePlan,
          planPeriod: planPeriod ?? '',
          coupon,
          contactInformation,
        })
      setFeatureCost(newFeatureCost)
      setFeatureCostWithTax(newFeatureCostWithTax)
      return preview
    } catch (err) {
      if (err.body?.error_code) {
        console.log('API error on AddOns.getPreview', err.body)
        const { error_code, error_msg } = err.body
        return {
          error: {
            error_code,
            error_msg,
          },
          line_items: [],
          total: null,
        }
      } else {
        return {} // TODO: don't silence errors
      }
    } finally {
      setLoadingPreview(false)
    }
  }

  useEffect(() => {
    getPreview()
  }, [value, billingProviderComponents])
  useEffect(() => {
    setOptions(tracksDynamicOptions.options)
  }, [tracksDynamicOptions.options])
  useEffect(() => {
    if (!tracksDynamicOptions.loading && !quotaUpdating) {
      tracksDynamicOptions.loadPreveiw()
    }
  }, [quotaUpdating])

  const onSubmitCouponCode = async coupon => {
    try {
      const preview = await getPreview(coupon)
      const newCouponComponent = {
        ...(preview?.current_billing_manifest?.line_items?.find(
          ({ kind }) => kind === 'coupon',
        ) || {}),
        coupon,
      }
      setCouponComponent(newCouponComponent)
      setOptions(removePriceInOptions(tracksDynamicOptions.options))
      return preview
    } catch (err) {
      console.log('API error at onSubmitCouponCode', err) // TODO: don't silence errors
    }
  }

  const onRemoveCouponCode = () => {
    setCouponComponent(null)
    setOptions(tracksDynamicOptions.options)
  }

  const getPrice = () => {
    // "couponComponent.amount_in_cents" contains a value with a minus sign
    // Chargebee returns the price in cents
    let result = (couponComponent?.amount_in_cents || 0) + featureCost

    if (planPeriod === 'annual') {
      result = result / 12
    }

    return result / 100
  }

  const renderUpgradeButton = () => {
    const validOption =
      quotaUsed > +(value?.value || 0)
        ? options.find(option => +option.value >= quotaUsed)
        : value

    if (!enableChange) {
      return (
        <Button
          appearance="primary"
          caption={
            <ContactUsLink
              className="AddOns_ContactUsLink"
              intercomMsg="I'd like to change my Discovery Suite subscription"
            >
              Upgrade
            </ContactUsLink>
          }
        />
      )
    }

    const query = new URLSearchParams({
      value: validOption?.value || '',
      price: String(validOption?.amount || ''),
      coupon: couponComponent?.coupon || '',
      type,
      isUpdate: String(quota),
      isModalExplorerRoute: String(isModalExplorerRoute),
      trackId: String(trackId),
    }).toString()
    return (
      <Button
        appearance="primary"
        caption={isModalExplorerRoute ? 'Add Discovery Suite' : 'Upgrade'}
        onClick={() => {
          setShowErrorAlert(false)
        }}
        link={
          isModalExplorerRoute
            ? `/add-ons-confirm-modal?${query}`
            : `/settings/billing/add-ons-confirm-modal?${query}`
        }
      />
    )
  }

  const renderSubmitButton = () => {
    if (!enableChange) {
      return <Button appearance="primary" caption={submitCaption} />
    }

    const isUpgrade = Boolean(quota)

    const query = new URLSearchParams({
      value: value?.value || '',
      price: String(
        isUpgrade ? Number(value?.amount) || 0 : featureCostWithTax / 100,
      ),
      coupon: couponComponent?.coupon || '',
      type,
      isUpdate: String(quota),
    }).toString()
    return (
      <Button
        appearance="primary"
        caption={submitCaption}
        onClick={() => {
          setShowErrorAlert(false)
        }}
        link={`/settings/billing/add-ons-confirm-modal?${query}`}
        disabled={submitDisabled}
        tooltip={
          disabledUpdatePaymentDetails
            ? 'Only admins on your account can make changes to your plan.'
            : undefined
        }
      />
    )
  }

  const renderCancelButton = () => {
    let cancelButton = 'Cancel Discovery Suite'
    let cancelIntercomMsg = "I'd like to cancel my discovery suite subscription"

    if (isWM) {
      cancelButton = 'Cancel Watermarking'
      cancelIntercomMsg = "I'd like to cancel my watermarking subscription"
    }

    if (!enableChange) {
      return (
        <ContactUsLink
          type="button"
          buttonType="danger"
          intercomMsg={cancelIntercomMsg}
        >
          {cancelButton}
        </ContactUsLink>
      )
    }

    return (
      <Button
        onClick={() => {
          setShowErrorAlert(false)
        }}
        link={`/settings/billing/add-ons-confirm-modal?value=0&type=${type}`}
        caption={cancelButton}
        appearance="danger"
      />
    )
  }

  const renderQuotaUsedMessage = () => {
    return (
      <>
        {quota || quotaUpdating ? (
          <div className="AddOns_Description">
            You have used{' '}
            <span
              style={
                overQuota
                  ? {
                      color: '#FC0000',
                    }
                  : undefined
              }
            >
              {Number(quotaUsed).toLocaleString()}
            </span>{' '}
            <span>
              <span>{isWM ? 'playlists on your' : 'of your current'}</span>{' '}
              <span>
                {quota === Infinity
                  ? 'unlimited'
                  : Number(quota).toLocaleString()}
              </span>
            </span>
            {quotaUpdating && <span> (updating)</span>}{' '}
            <span>
              {isWM
                ? 'playlist Watermarking subscription.'
                : 'track Discovery Suite subscription.'}
            </span>
            {Boolean(!overQuota && quota && enableChange && !showOptions) && (
              <span
                className="AddOns_ChangeSubscription"
                onClick={() => setShowOptions(true)}
              >
                Change subscription
              </span>
            )}
            {Boolean(!overQuota && quota && !enableChange) && (
              <ContactUsLink
                intercomMsg={
                  isWM
                    ? "I'd like to change my Watermarking subscription"
                    : "I'd like to change my Discovery Suite subscription"
                }
                className="AddOns_ChangeSubscription"
              >
                Change subscription
              </ContactUsLink>
            )}
            {overQuota && !isModalExplorerRoute && (
              <div className="AddOns_UpgradeButton">
                {renderUpgradeButton()}
              </div>
            )}
          </div>
        ) : null}
      </>
    )
  }

  const renderFeatureDescription = () => {
    if (isWM) {
      if (!quota) {
        return (
          <>
            Securely watermark your tracks with DISCO’s fast, secure
            watermarking. <br />
            The 1 watermarked playlist plan allows you to have one playlist
            watermarked at a time. If you’ll be watermarking multiple releases
            at any one time, choose the 3 playlist or unlimited plan.
            Watermarking is an add on addition to your base plan.
          </>
        )
      }

      return (
        <>
          DISCO Watermarking lets you secure your shared files with an inaudible
          watermark.
          <br />
          The Watermarking add on is in addition to your base plan and will be
          billed in the same billing cycle.
        </>
      )
    }

    return <DiscoverySuiteDescription />
  }

  return (
    <>
      {!isWM ? <div id="discovery-suite" /> : null}

      <Card
        title={
          <div className="AddOns_Title">
            <span>{isWM ? 'Watermarking' : 'Discovery Suite'}</span>{' '}
            <PricingCounter
              currencySymbol={loadingPreview ? '' : '$'}
              price={loadingPreview ? '...' : toCents(getPrice())}
              period="/month"
            />
          </div>
        }
        description={
          <>
            {disabled && (
              <div className="AddOns_AlertContainer">
                <Alert icon="warning" status="warning">
                  <div className="AddOns_AlertDescription">
                    You’re on a legacy base plan. To add{' '}
                    <span>{isWM ? 'Watermarking' : 'Discovery Suite'}</span>
                    <span>, you’ll need to upgrade your base plan. </span>
                    <ContactUsLink
                      className="AddOns_Link"
                      intercomMsg={`I'd like to change my base plan from ${
                        corePlan?.name
                      } and add ${isWM ? 'Watermarking' : 'Discovery Suite'}`}
                    >
                      {' '}
                      Contact us
                    </ContactUsLink>
                  </div>
                </Alert>
              </div>
            )}
            {showErrorAlert && (
              <div className="AddOns_AlertContainer">
                <Alert icon="warning" status="danger">
                  <div className="AddOns_AlertDescription">Payment failed.</div>
                  <Link
                    className="AddOns_AlertLink"
                    to="/settings/billing/update"
                  >
                    Update billing info
                  </Link>
                </Alert>
              </div>
            )}
            {renderQuotaUsedMessage()}
            {showOptions && (
              <>
                <div className="AddOns_LabelContainer">
                  <span className="AddOns_Label">
                    {isWM ? 'Playlists' : 'Tracks'}
                  </span>
                  {!isWM && (
                    <div className="AddOns_IconContainer">
                      <Tooltip
                        content="Your selected quota is applied separately for Auto tagging and Catalogs."
                        position="top"
                      >
                        <Icon
                          type="info-outline"
                          color="#42526E"
                          width={14}
                          height={14}
                          className="AddOns_Icon"
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
                <div className="AddOns_CustomDropdownContainer">
                  <CustomDropdown
                    disabled={disabled}
                    value={value}
                    options={options}
                    onChange={setValue}
                    placeholder="Select Tracks"
                  />
                </div>
              </>
            )}
            {renderFeatureDescription()}
            {showOptions && (
              <>
                <div>
                  <span
                    className="AddOns_CouponLink"
                    onClick={() => setOpenCoupon(!openCoupon)}
                  >
                    Add discount coupon
                  </span>
                  <Collapsible open={openCoupon} scrollIntoView>
                    <div className="AddOns_CouponContainer">
                      <Tooltip
                        content={
                          disabledUpdatePaymentDetails
                            ? 'Only admins on your account can make changes to your plan.'
                            : undefined
                        }
                      >
                        <CouponCode
                          submitDisabled={submitDisabled}
                          onSubmit={onSubmitCouponCode}
                          onRemove={onRemoveCouponCode}
                        />
                      </Tooltip>
                    </div>
                  </Collapsible>
                </div>
              </>
            )}
          </>
        }
        footerLinks={
          !isModalExplorerRoute && [
            {
              linkNode: (
                <div className="AddOns_SubmitButtonContainer">
                  <ButtonContainer>
                    {unsubscribeAvailable && showOptions && !overQuota && (
                      <Button
                        onClick={() => setShowOptions(false)}
                        caption="Cancel"
                      />
                    )}
                    {showOptions && renderSubmitButton()}
                    {unsubscribeAvailable &&
                      !(showOptions && !overQuota) &&
                      renderCancelButton()}
                  </ButtonContainer>
                </div>
              ),
            },
          ]
        }
      />
      {overQuota && isModalExplorerRoute && !isWM && (
        <div className="AddOns_UpgradeButton">{renderUpgradeButton()}</div>
      )}
    </>
  )
}

export default AddOns
